import { useCallback, useEffect, useRef, useState } from 'react';
import { approveCard, loadReviewCards, rejectCandidate } from '../features/review/api';
import type { ReviewCard } from '../features/review/candidates';
import { mediaUrl } from '../lib/storage';
import { label } from '../lib/vocab';
import { friendlyErrorMessage } from '../lib/errors';
import { useToast } from '../components/Toast';
import { Button } from '../components/Button';
import { Spinner } from '../components/Spinner';
import { ConfirmDialog } from '../components/ConfirmDialog';

export function ReviewPage() {
  const toast = useToast();
  const [cards, setCards] = useState<ReviewCard[] | null>(null);
  const [busyIds, setBusyIds] = useState<Set<string>>(new Set());
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const [lightbox, setLightbox] = useState<string | null>(null);
  const [confirmingApproveAll, setConfirmingApproveAll] = useState(false);
  const cardRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  const load = useCallback(() => {
    setCards(null);
    loadReviewCards()
      .then((data) => {
        setCards(data.cards);
        setFocusedId(data.cards[0]?.exerciseId ?? null);
      })
      .catch((e) => {
        setCards([]);
        toast.show(friendlyErrorMessage(e), 'error');
      });
  }, [toast]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function setBusy(exerciseId: string, busy: boolean) {
    setBusyIds((prev) => {
      const next = new Set(prev);
      if (busy) next.add(exerciseId);
      else next.delete(exerciseId);
      return next;
    });
  }

  function focusNextAfter(removedId: string) {
    setCards((prev) => {
      if (!prev) return prev;
      const index = prev.findIndex((c) => c.exerciseId === removedId);
      const remaining = prev.filter((c) => c.exerciseId !== removedId);
      const next = remaining[Math.min(index, remaining.length - 1)];
      setFocusedId(next?.exerciseId ?? null);
      return remaining;
    });
  }

  async function handleApprove(card: ReviewCard) {
    if (busyIds.has(card.exerciseId)) return;
    setBusy(card.exerciseId, true);
    try {
      await approveCard(card);
      focusNextAfter(card.exerciseId);
    } catch (e) {
      toast.show(friendlyErrorMessage(e), 'error');
    } finally {
      setBusy(card.exerciseId, false);
    }
  }

  async function handleReject(card: ReviewCard) {
    if (busyIds.has(card.exerciseId)) return;
    setBusy(card.exerciseId, true);
    try {
      await rejectCandidate(card.candidatePath);
      focusNextAfter(card.exerciseId);
    } catch (e) {
      toast.show(friendlyErrorMessage(e), 'error');
    } finally {
      setBusy(card.exerciseId, false);
    }
  }

  async function handleApproveAll() {
    setConfirmingApproveAll(false);
    if (!cards) return;
    const toApprove = cards;
    for (const card of toApprove) {
      setBusy(card.exerciseId, true);
    }
    const failed: ReviewCard[] = [];
    for (const card of toApprove) {
      try {
        await approveCard(card);
      } catch (e) {
        failed.push(card);
        toast.show(`${card.exercise?.name_en ?? card.exerciseId}: ${friendlyErrorMessage(e)}`, 'error');
      }
    }
    setCards(failed);
    setFocusedId(failed[0]?.exerciseId ?? null);
    setBusyIds(new Set());
    if (failed.length === 0) toast.show('Approved every candidate on screen.', 'success');
  }

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (lightbox || confirmingApproveAll) return;
      const target = e.target as HTMLElement | null;
      if (target && /input|textarea|select/i.test(target.tagName)) return;
      if (!focusedId || !cards) return;
      const card = cards.find((c) => c.exerciseId === focusedId);
      if (!card) return;
      if (e.key === 'a' || e.key === 'A') {
        e.preventDefault();
        handleApprove(card);
      } else if (e.key === 'r' || e.key === 'R') {
        e.preventDefault();
        handleReject(card);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusedId, cards, lightbox, confirmingApproveAll, busyIds]);

  useEffect(() => {
    if (!focusedId) return;
    cardRefs.current.get(focusedId)?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [focusedId]);

  return (
    <div>
      <div className="topbar">
        <h1 className="page-title">Review</h1>
        {cards && cards.length > 0 ? (
          <Button tone="tonal" onClick={() => setConfirmingApproveAll(true)}>
            Approve all on screen ({cards.length})
          </Button>
        ) : null}
      </div>

      {cards === null ? (
        <Spinner label="Loading candidates…" />
      ) : cards.length === 0 ? (
        <p className="muted">No candidates waiting.</p>
      ) : (
        <div className="review-grid">
          {cards.map((card) => {
            const url = mediaUrl(card.candidatePath);
            const existingUrl = mediaUrl(card.existingDemoPath);
            const busy = busyIds.has(card.exerciseId);
            const name = card.exercise?.name_en ?? card.exerciseId;
            return (
              <div
                key={card.exerciseId}
                ref={(el) => {
                  if (el) cardRefs.current.set(card.exerciseId, el);
                  else cardRefs.current.delete(card.exerciseId);
                }}
                className={`review-card ${focusedId === card.exerciseId ? 'focused' : ''}`}
                onClick={() => setFocusedId(card.exerciseId)}
              >
                <div className="review-card-image-wrap" onClick={() => url && setLightbox(url)}>
                  {url ? <img src={url} alt="" /> : null}
                  {existingUrl ? <img className="review-card-existing" src={existingUrl} alt="Current demo photo" title="Current demo photo" /> : null}
                </div>
                <div className="review-card-meta">
                  <span className="review-card-name">{name}</span>
                  {card.exercise ? (
                    <>
                      <span className="muted" dir="auto">
                        {card.exercise.name_he}
                      </span>
                      <span className="muted">
                        {label(card.exercise.sport)} · {label(card.exercise.muscle_group)}
                      </span>
                    </>
                  ) : (
                    <span className="muted">Exercise no longer exists</span>
                  )}
                </div>
                <div className="review-card-actions">
                  <Button tone="danger" size="sm" disabled={busy} onClick={() => handleReject(card)}>
                    Reject (R)
                  </Button>
                  <Button tone="accent" size="sm" disabled={busy} onClick={() => handleApprove(card)}>
                    {busy ? 'Working…' : 'Approve (A)'}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {lightbox ? (
        <div className="review-lightbox" onClick={() => setLightbox(null)}>
          <img src={lightbox} alt="" />
        </div>
      ) : null}

      <ConfirmDialog
        open={confirmingApproveAll}
        title="Approve all on screen?"
        message={`This uploads ${cards?.length ?? 0} candidate${cards?.length === 1 ? '' : 's'} as demo photos and removes the candidates.`}
        confirmLabel="Approve all"
        tone="accent"
        onConfirm={handleApproveAll}
        onCancel={() => setConfirmingApproveAll(false)}
      />
    </div>
  );
}
